import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Avatar,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  EmojiEvents as EmojiEventsIcon,
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon,
  DateRange as DateRangeIcon,
  Send as SendIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';

import leaderboardService from '../../services/leaderboard.service';

function Winners() {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [winners, setWinners] = useState([]);
  const [previousWinners, setPreviousWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [winnerLoading, setWinnerLoading] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [notifySuccess, setNotifySuccess] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [notifyDialog, setNotifyDialog] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');

  useEffect(() => {
    fetchLeaderboardAndWinners();
  }, [selectedDate]);

  const fetchLeaderboardAndWinners = async () => {
    setLoading(true);
    setError(null);

    try {
      // Format date for API
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      // Fetch leaderboard for selected date
      const leaderboardResponse = await leaderboardService.getDailyLeaderboard(dateStr);
      setLeaderboard(leaderboardResponse.data.leaderboard);
      
      // Fetch existing winners for selected date
      const winnersResponse = await leaderboardService.getWinnersByDate(dateStr);
      setPreviousWinners(winnersResponse.data.winners);
      
      // Pre-select top 3 if no winners are set yet
      if (winnersResponse.data.winners.length === 0 && leaderboardResponse.data.leaderboard.length > 0) {
        setWinners(leaderboardResponse.data.leaderboard.slice(0, 3).map((participant, index) => ({
          ...participant,
          position: index + 1,
          prize_amount: getPrizeAmount(index + 1)
        })));
      } else {
        setWinners(winnersResponse.data.winners);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getPrizeAmount = (position) => {
    switch(position) {
      case 1:
        return 300; // First place: 300 GHS
      case 2:
        return 200; // Second place: 200 GHS
      case 3:
        return 100; // Third place: 100 GHS
      default:
        return 0;
    }
  };

  const handleSaveWinners = async () => {
    setWinnerLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await leaderboardService.saveWinners(dateStr, winners);
      setSuccess(true);
      setConfirmDialog(false);
      setPreviousWinners(winners);
    } catch (err) {
      console.error('Failed to save winners:', err);
      setError('Failed to save winners. Please try again.');
    } finally {
      setWinnerLoading(false);
    }
  };

  const handleNotifyWinners = async () => {
    setNotifyLoading(true);
    setError(null);
    setNotifySuccess(false);
    
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await leaderboardService.notifyWinners(dateStr, notifyMessage);
      setNotifySuccess(true);
      setNotifyDialog(false);
    } catch (err) {
      console.error('Failed to notify winners:', err);
      setError('Failed to notify winners. Please try again.');
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const openConfirmDialog = () => {
    setConfirmDialog(true);
  };

  const openNotifyDialog = () => {
    const dateFormatted = format(selectedDate, 'MMMM d, yyyy');
    
    // Default notification message
    setNotifyMessage(
      `Congratulations! You are one of the winners for the Adwuma Adwuma Trivia Game on ${dateFormatted}. Please contact our team at 0302123456 to claim your prize.`
    );
    
    setNotifyDialog(true);
  };

  const areWinnersSelected = winners.length > 0;
  const areWinnersSaved = previousWinners.length > 0;
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/leaderboard')}
          sx={{ mb: 2 }}
        >
          Back to Leaderboard
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          Select Winners
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select and manage daily trivia game winners
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Winners saved successfully!
        </Alert>
      )}
      
      {notifySuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Winners have been notified successfully!
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6">
            <DateRangeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Select Date
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              renderInput={(params) => <TextField {...params} size="small" />}
              maxDate={new Date()}
            />
          </LocalizationProvider>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1">
            {format(selectedDate, 'MMMM d, yyyy')} Winners
          </Typography>
          
          <Box>
            {areWinnersSaved && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SendIcon />}
                onClick={openNotifyDialog}
                sx={{ mr: 2 }}
              >
                Notify Winners
              </Button>
            )}
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckIcon />}
              onClick={openConfirmDialog}
              disabled={!areWinnersSelected || loading}
            >
              Save Winners
            </Button>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Winner Podium */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom>
            Winner's Podium
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : winners.length === 0 ? (
            <Alert severity="info">
              No winners selected yet. Select winners from the leaderboard.
            </Alert>
          ) : (
            <Box sx={{ position: 'relative', height: 380, mt: 4 }}>
              {winners.map((winner, index) => {
                if (index > 2) return null; // Only show top 3
                
                // Podium configuration
                const positions = [
                  { order: 1, height: 180, top: 200, left: '36%', width: 100 },
                  { order: 0, height: 220, top: 160, left: '8%', width: 100 },
                  { order: 2, height: 140, top: 240, left: '64%', width: 100 }
                ];
                
                const pos = positions.find(p => p.order === index);
                if (!pos) return null;
                
                return (
                  <Box
                    key={winner.user_id}
                    sx={{
                      position: 'absolute',
                      top: pos.top,
                      left: pos.left,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: pos.width,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        mb: 1,
                        border: '3px solid',
                        borderColor: 
                          index === 0 ? 'warning.main' : 
                          index === 1 ? 'grey.400' : 
                          'brown.300'
                      }}
                    >
                      {winner.name ? winner.name.charAt(0) : winner.msisdn.charAt(0)}
                    </Avatar>
                    <Typography variant="body2" align="center" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {winner.name || winner.msisdn}
                    </Typography>
                    <Typography variant="body2" align="center" sx={{ mb: 0.5 }}>
                      {winner.total_points} pts
                    </Typography>
                    <Chip
                      label={`${winner.prize_amount} GHS`}
                      color={index === 0 ? 'warning' : index === 1 ? 'secondary' : 'default'}
                      size="small"
                    />
                    <Box
                      sx={{
                        height: pos.height,
                        width: '100%',
                        bgcolor: 
                          index === 0 ? 'warning.main' : 
                          index === 1 ? 'grey.400' : 
                          'brown.300',
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                        mt: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="h5" color="white" fontWeight="bold">
                        {index + 1}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Grid>
        
        {/* Leaderboard */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>
            Leaderboard
          </Typography>
          
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 380 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                  <CircularProgress />
                </Box>
              ) : leaderboard.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No entries found for this date.
                  </Typography>
                </Box>
              ) : (
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell>Entries</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaderboard.map((entry, index) => {
                      // Check if already in winners
                      const isWinner = winners.some(w => w.user_id === entry.user_id);
                      
                      return (
                        <TableRow 
                          key={entry.user_id}
                          sx={{
                            backgroundColor: isWinner ? 'rgba(255, 82, 82, 0.05)' : 'transparent',
                          }}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="body2" fontWeight={isWinner ? 'bold' : 'normal'}>
                                {entry.name || 'Anonymous User'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {entry.msisdn}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={isWinner ? 'bold' : 'normal'} color={isWinner ? 'primary.main' : 'inherit'}>
                              {entry.total_points}
                            </Typography>
                          </TableCell>
                          <TableCell>{entry.entry_count}</TableCell>
                          <TableCell align="right">
                            {isWinner ? (
                              <Chip 
                                label="Selected" 
                                color="primary"
                                size="small"
                                icon={<CheckIcon />}
                              />
                            ) : (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PersonAddIcon />}
                                onClick={() => {
                                  // Add to winners at next available position
                                  const position = winners.length + 1;
                                  setWinners([
                                    ...winners, 
                                    {
                                      ...entry,
                                      position,
                                      prize_amount: getPrizeAmount(position)
                                    }
                                  ]);
                                }}
                                disabled={winners.length >= 3}
                              >
                                Select
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Confirm Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Winners</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to save these winners for {format(selectedDate, 'MMMM d, yyyy')}? 
            {previousWinners.length > 0 && " This will overwrite previously saved winners."}
          </DialogContentText>
          {winners.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Selected Winners:</Typography>
              {winners.map((winner, index) => (
                <Box key={winner.user_id} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Chip 
                    label={index + 1} 
                    size="small" 
                    color={
                      index === 0 ? 'warning' : 
                      index === 1 ? 'secondary' : 
                      'default'
                    }
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2">
                    {winner.name || winner.msisdn} - {winner.total_points} points - {winner.prize_amount} GHS
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveWinners} 
            variant="contained"
            color="primary"
            startIcon={winnerLoading ? <CircularProgress size={16} /> : null}
            disabled={winnerLoading}
          >
            {winnerLoading ? 'Saving...' : 'Save Winners'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notify Dialog */}
      <Dialog 
        open={notifyDialog} 
        onClose={() => setNotifyDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Notify Winners</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Send SMS notifications to the winners for {format(selectedDate, 'MMMM d, yyyy')}.
          </DialogContentText>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notification Message"
            value={notifyMessage}
            onChange={(e) => setNotifyMessage(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          {previousWinners.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Recipients:</Typography>
              {previousWinners.map((winner, index) => (
                <Box key={winner.user_id} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Chip 
                    label={index + 1} 
                    size="small" 
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2">
                    {winner.name || 'Anonymous User'} ({winner.msisdn})
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleNotifyWinners} 
            variant="contained"
            color="primary"
            startIcon={notifyLoading ? <CircularProgress size={16} /> : <SendIcon />}
            disabled={notifyLoading}
          >
            {notifyLoading ? 'Sending...' : 'Send Notifications'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Winners;